/**
 * CFB Events-tab rows (cfb-game-props) — the ONE description of how an enum
 * outcome or a yes/no event becomes a tradable synthetic GameRow, shared by
 * the table (GameLinesSection) and the parent's live-ticket resolver
 * (GameDetail/index.tsx `livePanelGame`).
 *
 * Danielle DNL-001 (2026-08-30): the click-time synth is a snapshot; the
 * panel must re-resolve the row from the CURRENT store result on every
 * frame or the ticket's book freezes. `findLiveCfbEventSynth` is that
 * resolver — it walks the live `CfbEventMarkets` by the row's real ticker
 * (the last segment of the synth key) and rebuilds the synth with the same
 * wording, so the panel's K books tick without another click. A missing
 * selector result or ticker returns null so the parent fails closed instead
 * of leaving an obsolete click snapshot actionable.
 */
import type { CfbEventEnumGroup, CfbEventMarkets, CfbEventOutcome, GameRow, VenueBook } from '@/views/ValueDashboard/types';
import { BOTH_TEAMS_POINTS_LABEL } from '@/lib/cfbWireMarkets';

import { CFB_EVENT_SYNTH_MARKER as KEY_TAG, cfbEventNoLabel } from '@/lib/sport/cfbEventSynthKey';
export { cfbEventSynthKey } from '@/lib/sport/cfbEventSynthKey';

/** Synth key: `<gameKey>|cfb-<class>-<ticker>` — the ticker is the stable
 *  identity the live resolver re-finds. */

export function isCfbEventSynthKey(gameKey: string, synthKey: string): boolean {
  return synthKey.startsWith(`${gameKey}${KEY_TAG}`);
}

/** The subtitle already names the family for first-to-score / first-TD /
 *  half-fulltime rows; a bare quarter name needs its pane title. */
export function cfbEnumRowLabel(group: CfbEventEnumGroup, outcome: CfbEventOutcome): string {
  return group.cls === 'highest-quarter' ? `${group.label}: ${outcome.label}` : outcome.label;
}

/** One Kalshi binary as a single-market-both-slots row: the away slot is the
 *  YES wording, the home slot its complement (the order ticket reads them
 *  as YES/NO, #251). */
export function buildCfbEventSynth(
  game: GameRow,
  key: string,
  yesLabel: string,
  noLabel: string,
  kalshi: VenueBook,
): GameRow {
  return {
    ...game,
    key,
    gameState: 'pre',
    finalWinnerSide: null,
    awayName: yesLabel,
    homeName: noLabel,
    kalshi: { away: kalshi, home: kalshi },
    polymarket: null,
  };
}

/** Re-resolve an enum/binary synth from the live selector result. Null when
 *  the selector has no current result, the key is not a CFB event row of this
 *  game, or the market left the feed. O/U ladder pills also arrive here when
 *  clicked directly, without first opening an expanded ladder. */
export function findLiveCfbEventSynth(game: GameRow, synthKey: string, ev: CfbEventMarkets | null): GameRow | null {
  if (!ev) return null;
  if (!isCfbEventSynthKey(game.key, synthKey)) return null;
  const rest = synthKey.slice(game.key.length + KEY_TAG.length);
  // Match the complete table key, including team, period and exact rung.
  // Never revive the click-time book when the current selector loses a rung.
  for (const ladder of ev.teamStats) {
    const period = ladder.period === 'full' ? '' : `-${ladder.period}`;
    const teamBase = ladder.teamSide === 'away'
      ? game.awayName || game.awayCode : game.homeName || game.homeCode;
    const team = `${teamBase}${ladder.period === 'full' ? '' : ` (${ladder.period})`}`;
    for (const line of ladder.lines) {
      if (rest === `teamstat-${ladder.stat}-${ladder.teamCode}${period}-${line.line}`) {
        return buildCfbEventSynth(game, synthKey,
          `${team} Over ${line.line} ${ladder.statLabel}`,
          `${team} Under ${line.line} ${ladder.statLabel}`, line.kalshi);
      }
    }
  }
  for (const ladder of ev.totals) {
    for (const line of ladder.lines) {
      if (rest === `${ladder.cls}-${line.line}`) {
        return buildCfbEventSynth(game, synthKey,
          `Over ${line.line} ${ladder.label}`, `Under ${line.line} ${ladder.label}`, line.kalshi);
      }
    }
  }
  for (const line of ev.bothTeamsPoints) {
    if (rest === `both-teams-points-${line.line}`) {
      return buildCfbEventSynth(game, synthKey,
        `${BOTH_TEAMS_POINTS_LABEL} ${line.line}+`, `Not both teams ${line.line}+`, line.kalshi);
    }
  }
  for (const g of ev.enums) {
    for (const o of g.outcomes) {
      if (rest === `${g.cls}-${o.ticker}`) {
        const label = cfbEnumRowLabel(g, o);
        return buildCfbEventSynth(game, synthKey, label, cfbEventNoLabel(label), o.kalshi);
      }
    }
  }
  for (const b of ev.binaries) {
    if (rest === `${b.cls}-${b.ticker}`) {
      return buildCfbEventSynth(game, synthKey, b.label, cfbEventNoLabel(b.label), b.kalshi);
    }
  }
  // NFL race-to legs (nfl-game-lines-props) — same snapshot rule.
  for (const line of ev.races) {
    for (const o of line.outcomes) {
      if (rest === `race-to-${o.ticker}`) {
        return buildCfbEventSynth(game, synthKey, o.label, cfbEventNoLabel(o.label), o.kalshi);
      }
    }
  }
  return null;
}

/** Parent-panel boundary for CFB event rows. `undefined` means the key is not
 *  a CFB event synth and the caller should continue through its other live-row
 *  resolvers. `null` means it is a CFB event synth but its base game, selector
 *  result, or exact ticker is no longer current, so the trading panel must fail
 *  closed rather than revive the click-time snapshot. The selector remains
 *  lazy for every unrelated ticket. */
export function resolveLiveCfbEventPanelGame(
  game: GameRow | null,
  synthKey: string,
  getEventMarkets: (gameKey: string) => CfbEventMarkets | null,
): GameRow | null | undefined {
  const marker = synthKey.indexOf(KEY_TAG);
  if (marker < 0) return undefined;

  const baseGameKey = synthKey.slice(0, marker);
  if (!game || game.key !== baseGameKey) return null;

  return findLiveCfbEventSynth(game, synthKey, getEventMarkets(game.key));
}
